class WhatsAppService {
  constructor() {
    this.provider = process.env.REACT_APP_WHATSAPP_PROVIDER || 'meta'; // 'meta', 'twilio', or 'gupshup'
    this.baseURL = this.getBaseURL();
    this.backendUrl = process.env.REACT_APP_WHATSAPP_BACKEND_URL || 'http://localhost:5001';
    this.businessAccountId = process.env.REACT_APP_WHATSAPP_BUSINESS_ACCOUNT_ID;
  }

  getBaseURL() {
    switch (this.provider) {
      case 'meta':
        return 'https://graph.facebook.com/v18.0';
      case 'twilio':
        return 'https://api.twilio.com/2010-04-01';
      case 'gupshup':
        return 'https://api.gupshup.io/sm/api/v1';
      default:
        return 'https://graph.facebook.com/v18.0';
    }
  }

  /**
   * Format phone number to international format
   * @param {string} phone - Phone number
   * @returns {string} - Formatted phone number
   */
  formatPhoneNumber(phone) {
    if (!phone) return null;

    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');

    // If it's a 10-digit number without country code, assume India (+91)
    if (cleaned.length === 10 && !cleaned.startsWith('91')) {
      cleaned = '91' + cleaned;
    }

    // Return the cleaned number (should already have country code for international numbers)
    return cleaned;
  }

  /**
   * Send WhatsApp message using template
   * @param {Object} params - Message parameters
   * @returns {Promise} - API response
   */
  async sendTemplateMessage(params) {
    const { to, templateName, templateLanguage = 'en', components = [] } = params;

    const formattedPhone = this.formatPhoneNumber(to);

    // Debug logging
    console.log('📱 WhatsApp Service - Original phone:', to);
    console.log('📱 WhatsApp Service - Formatted phone:', formattedPhone);

    if (!formattedPhone) {
      throw new Error('Invalid phone number');
    }

    try {
      let response;

      switch (this.provider) {
        case 'meta':
          response = await this.sendMetaTemplate(formattedPhone, templateName, templateLanguage, components);
          break;
        case 'twilio':
          response = await this.sendTwilioTemplate(formattedPhone, templateName, components);
          break;
        case 'gupshup':
          response = await this.sendGupshupTemplate(formattedPhone, templateName, components);
          break;
        default:
          throw new Error('Unsupported WhatsApp provider');
      }

      return response;
    } catch (error) {
      console.error('WhatsApp send error:', error);
      throw error;
    }
  }

  /**
   * Send template via Node backend → Meta WhatsApp Cloud API.
   * Token stays server-side; frontend never touches it.
   */
  async sendMetaTemplate(to, templateName, language, components) {
    const response = await fetch(`${this.backendUrl}/api/send-whatsapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to,
        template: templateName,
        langCode: language,
        components,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to send WhatsApp message');
    }

    return data.data;
  }

  /**
   * Send template via Twilio
   */
  async sendTwilioTemplate(to, templateName, components) {
    const accountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
    const authToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
    const fromNumber = process.env.REACT_APP_TWILIO_WHATSAPP_NUMBER;

    const url = `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Messages.json`;

    // Build message body from components
    let body = '';
    components.forEach(comp => {
      if (comp.type === 'body' && comp.parameters) {
        comp.parameters.forEach(param => {
          body += param.text + ' ';
        });
      }
    });

    const params = new URLSearchParams();
    params.append('From', fromNumber);
    params.append('To', `whatsapp:${to}`);
    params.append('Body', body.trim());

    const credentials = btoa(`${accountSid}:${authToken}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send Twilio WhatsApp message');
    }

    return await response.json();
  }

  /**
   * Send template via Gupshup
   */
  async sendGupshupTemplate(to, templateName, components) {
    const apiKey = process.env.REACT_APP_GUPSHUP_API_KEY;
    const appName = process.env.REACT_APP_GUPSHUP_APP_NAME;

    const url = `${this.baseURL}/template/msg`;

    // Build parameters from components
    const params = [];
    components.forEach(comp => {
      if (comp.type === 'body' && comp.parameters) {
        comp.parameters.forEach(param => {
          params.push(param.text);
        });
      }
    });

    const payload = new URLSearchParams({
      channel: 'whatsapp',
      source: appName,
      destination: to,
      'src.name': appName,
      template: JSON.stringify({
        id: templateName,
        params: params
      })
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': apiKey,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: payload.toString()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send Gupshup WhatsApp message');
    }

    return await response.json();
  }

  /**
   * Create message template (Meta only)
   */
  async createTemplate(templateData) {
    if (this.provider !== 'meta') {
      throw new Error('Template creation only supported for Meta provider');
    }

    const url = `${this.baseURL}/${this.businessAccountId}/message_templates`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create template');
    }

    return await response.json();
  }

  /**
   * Get all templates (Meta only)
   */
  async getTemplates() {
    if (this.provider !== 'meta') {
      throw new Error('Template retrieval only supported for Meta provider');
    }

    const url = `${this.baseURL}/${this.businessAccountId}/message_templates`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to get templates');
    }

    return await response.json();
  }

  /**
   * Delete template (Meta only)
   */
  async deleteTemplate(templateName) {
    if (this.provider !== 'meta') {
      throw new Error('Template deletion only supported for Meta provider');
    }

    const url = `${this.baseURL}/${this.businessAccountId}/message_templates`;

    const deleteUrl = `${url}?name=${encodeURIComponent(templateName)}`;

    const response = await fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to delete template');
    }

    return await response.json();
  }

  /**
   * Send invoice notification using the approved 'invoice' template.
   * Template body: "Hello {{1}}, Thank ..."
   *   {{1}} = customerName
   * @param {Object} invoiceData - Invoice details
   */
  async sendInvoiceNotification(invoiceData) {
    const {
      customerName,
      customerPhone,
      invoiceNumber,
      invoiceDate,
      totalAmount,
      paymentStatus,
      invoiceUrl,
    } = invoiceData;

    const components = [
      {
        type: 'body',
        parameters: [
          { type: 'text', text: String(customerName  || '') },
          { type: 'text', text: String(invoiceNumber || '') },
          { type: 'text', text: String(invoiceDate   || '') },
          { type: 'text', text: String(totalAmount   || '') },
          { type: 'text', text: String(paymentStatus || '') },
          { type: 'text', text: String(invoiceUrl    || '') },
        ],
      },
    ];

    return await this.sendTemplateMessage({
      to: customerPhone,
      templateName: 'invoice',
      templateLanguage: 'en',
      components,
    });
  }

  /**
   * Send sale notification
   * @param {Object} saleData - Sale details
   */
  async sendSaleNotification(saleData) {
    const {
      customerName,
      customerPhone,
      saleNumber,
      amount,
      currency = 'USD',
      items
    } = saleData;

    const templateName = 'sale_notification';

    const components = [
      {
        type: 'body',
        parameters: [
          {
            type: 'text',
            text: customerName
          },
          {
            type: 'text',
            text: saleNumber
          },
          {
            type: 'text',
            text: `${currency} ${amount}`
          },
          {
            type: 'text',
            text: items || 'your items'
          }
        ]
      }
    ];

    return await this.sendTemplateMessage({
      to: customerPhone,
      templateName: templateName,
      templateLanguage: 'en',
      components: components
    });
  }
}

export default new WhatsAppService();
